"use client"
import { useState } from "react"
import { Link } from "react-router-dom"
import Footer from "./Footer"

const FAQ = () => {
  // FAQ categories
  const categories = ["General", "Pricing", "Technical", "Account", "Privacy & Security"]

  const [activeCategory, setActiveCategory] = useState("General")

  // FAQ items organized by category
  const faqItems = {
    General: [
      {
        question: "What is Shopify Scraper?",
        answer:
          "Shopify Scraper is a powerful tool that allows you to extract product data from any Shopify store. It helps e-commerce professionals save time by automating the process of collecting product information for research, competitive analysis, or importing into their own stores.",
      },
      {
        question: "Is Shopify Scraper legal to use?",
        answer:
          "Yes, Shopify Scraper only extracts publicly available information from Shopify stores. However, always ensure you comply with the terms of service of the websites you visit and use the extracted data responsibly and ethically.",
      },
      {
        question: "How do I get started with Shopify Scraper?",
        answer:
          "Getting started is easy! Simply download our Chrome extension, create an account, and you can immediately begin extracting product data from any Shopify store. Our user-friendly interface makes the process simple and intuitive.",
      },
      {
        question: "Can I use Shopify Scraper on any browser?",
        answer:
          "Currently, Shopify Scraper is available as a Chrome extension. We are working on supporting other browsers in the future, but for now, you'll need to use Google Chrome or any Chromium-based browser like Edge or Brave.",
      },
      {
        question: "Do you offer customer support?",
        answer:
          "Yes, we provide customer support via email for all users. Premium plan subscribers receive priority support with faster response times. You can reach our support team at support@shopifyscraper.com.",
      },
    ],
    Pricing: [
      {
        question: "Is there a free version of Shopify Scraper?",
        answer:
          "Yes, we offer a free plan that allows you to export up to 50 products per day. This is perfect for individuals or small businesses with limited needs. For more extensive usage, we recommend our premium plans.",
      },
      {
        question: "What payment methods do you accept?",
        answer:
          "We accept all major credit cards, including Visa, Mastercard, American Express, and Discover. We also support PayPal payments for your convenience.",
      },
      {
        question: "Can I upgrade or downgrade my plan?",
        answer:
          "Yes, you can upgrade or downgrade your plan at any time through your account dashboard. When upgrading, you'll be charged the prorated difference. When downgrading, the new rate will apply at the start of your next billing cycle.",
      },
      {
        question: "Do you offer refunds?",
        answer:
          "We offer a 14-day money-back guarantee for all paid plans. If you're not satisfied with our service, you can request a full refund within 14 days of your purchase by contacting our support team.",
      },
      {
        question: "Is there a discount for annual billing?",
        answer:
          "Yes, we offer a 20% discount when you choose annual billing instead of monthly billing. This is a great way to save money if you plan to use Shopify Scraper long-term.",
      },
    ],
    Technical: [
      {
        question: "How many products can I export at once?",
        answer:
          "The number of products you can export depends on your plan. Free users can export up to 50 products per day, while our premium plans offer higher or unlimited export limits. Check our pricing page for detailed information.",
      },
      {
        question: "What data can I extract with Shopify Scraper?",
        answer:
          "Shopify Scraper can extract product titles, descriptions, images, prices, variants, collections, tags, and more. Premium plans offer additional data fields and more comprehensive information.",
      },
      {
        question: "In what format can I export the data?",
        answer:
          "Currently, we support CSV export format, which is compatible with both Shopify and WooCommerce platforms. We're working on adding more export formats in future updates.",
      },
      {
        question: "Does Shopify Scraper work on all Shopify stores?",
        answer:
          "Yes, Shopify Scraper works on all public Shopify stores. However, some stores may have implemented measures to limit data extraction, which could affect the tool's performance in those specific cases.",
      },
      {
        question: "Can I schedule automated exports?",
        answer:
          "Automated exports are available on our Business plan. This feature allows you to schedule regular exports from specific stores to keep your data up-to-date without manual intervention.",
      },
    ],
    Account: [
      {
        question: "How do I create an account?",
        answer:
          'You can create an account by clicking the "Sign Up" button on our website or when you first install the Chrome extension. You\'ll need to provide your email address and create a password.',
      },
      {
        question: "Can I have multiple users on one account?",
        answer:
          "Multiple user accounts are available on our Business plan. This allows team collaboration with separate logins but shared data and export limits.",
      },
      {
        question: "How do I reset my password?",
        answer:
          'You can reset your password by clicking the "Forgot Password" link on the login page. We\'ll send you an email with instructions to create a new password.',
      },
      {
        question: "How do I cancel my subscription?",
        answer:
          'You can cancel your subscription at any time through your account dashboard. Go to "Billing" and click on "Cancel Subscription." Your access will continue until the end of your current billing period.',
      },
      {
        question: "Can I transfer my subscription to another account?",
        answer:
          "Subscription transfers are not automatically available. Please contact our support team if you need to transfer your subscription to another account, and we'll assist you with the process.",
      },
    ],
    "Privacy & Security": [
      {
        question: "Is my data secure with Shopify Scraper?",
        answer:
          "Yes, we take data security seriously. All data is encrypted in transit and at rest. We use industry-standard security measures to protect your information and the data you extract.",
      },
      {
        question: "Do you store the data I extract?",
        answer:
          "We temporarily store your extracted data to make it available for download. Data is automatically deleted after 30 days for free users and 90 days for premium users, unless you choose to delete it earlier.",
      },
      {
        question: "What permissions does the Chrome extension require?",
        answer:
          "Our Chrome extension requires permission to access the current tab when you activate it on a Shopify store. It does not collect any personal information or track your browsing activity on non-Shopify websites.",
      },
      {
        question: "Do you share my information with third parties?",
        answer:
          "We do not sell or share your personal information with third parties for marketing purposes. We may use trusted third-party services for processing payments or providing customer support, but these partners are bound by strict confidentiality agreements.",
      },
      {
        question: "How can I delete my account and data?",
        answer:
          "You can request account deletion through your account settings or by contacting our support team. When you delete your account, all your personal information and stored data will be permanently removed from our systems.",
      },
    ],
  }

  return (
    <div className="faq-page">
      {/* Hero Section */}
      <section className="faq-hero">
        <div className="faq-hero-container">
          <h1 className="faq-hero-title">Frequently Asked Questions</h1>
          <p className="faq-hero-description">Find answers to common questions about Shopify Scraper</p>
        </div>
      </section>

      {/* FAQ Content */}
      <section className="faq-content-section">
        <div className="faq-content-container">
          {/* Category Navigation */}
          <div className="faq-categories">
            {categories.map((category) => (
              <button
                key={category}
                className={`category-button ${activeCategory === category ? "active" : ""}`}
                onClick={() => setActiveCategory(category)}
              >
                {category}
              </button>
            ))}
          </div>

          {/* FAQ Items */}
          <div className="faq-items">
            <h2 className="category-title">{activeCategory} Questions</h2>
            <div className="faq-list">
              {faqItems[activeCategory].map((item, index) => (
                <div className="faq-item" key={index}>
                  <h3 className="faq-question">{item.question}</h3>
                  <p className="faq-answer">{item.answer}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Styling */}
      <style jsx="true">{`
        /* FAQ Hero Section */
        .faq-hero {
          background-color: #007BFF;
          color: white;
          padding: 5rem 0;
          text-align: center;
        }
        
        .faq-hero-container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 1.5rem;
        }
        
        .faq-hero-title {
          font-size: 2.5rem;
          font-weight: 700;
          margin-bottom: 1.5rem;
        }
        
        .faq-hero-description {
          font-size: 1.25rem;
          max-width: 800px;
          margin: 0 auto;
          opacity: 0.9;
        }
        
        /* FAQ Content Section */
        .faq-content-section {
          padding: 5rem 0;
          background-color: #f9fafb;
        }
        
        .faq-content-container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 1.5rem;
          display: flex;
          gap: 3rem;
        }
        
        /* Category Navigation */
        .faq-categories {
          width: 250px;
          flex-shrink: 0;
        }
        
        .category-button {
          display: block;
          width: 100%;
          text-align: left;
          padding: 1rem;
          margin-bottom: 0.5rem;
          background-color: white;
          border: 1px solid #e5e7eb;
          border-radius: 0.375rem;
          font-size: 1rem;
          font-weight: 500;
          color: #4a5568;
          cursor: pointer;
          transition: all 0.2s;
        }
        
        .category-button:hover {
          background-color: #f3f4f6;
        }
        
        .category-button.active {
          background-color: #007BFF;
          color: white;
          border-color: #007BFF;
        }
        
        /* FAQ Items */
        .faq-items {
          flex: 1;
        }
        
        .category-title {
          font-size: 1.75rem;
          font-weight: 700;
          margin-bottom: 2rem;
          color: #1a202c;
        }
        
        .faq-list {
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
        }
        
        .faq-item {
          background-color: white;
          padding: 1.5rem;
          border-radius: 0.5rem;
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
        }
        
        .faq-question {
          font-size: 1.25rem;
          font-weight: 600;
          margin-bottom: 1rem;
          color: #1a202c;
        }
        
        .faq-answer {
          font-size: 1rem;
          color: #4a5568;
          line-height: 1.6;
        }
        
        /* Responsive Styles */
        @media (max-width: 1024px) {
          .faq-content-container {
            flex-direction: column;
          }
          
          .faq-categories {
            width: 100%;
            display: flex;
            flex-wrap: wrap;
            gap: 0.5rem;
          }
          
          .category-button {
            width: auto;
            flex: 1;
            text-align: center;
            padding: 0.75rem;
            margin-bottom: 0;
          }
        }
        
        @media (max-width: 768px) {
          .faq-hero-title {
            font-size: 2rem;
          }
          
          .faq-hero-description {
            font-size: 1.125rem;
          }
          
          .category-title {
            font-size: 1.5rem;
          }
          
          .faq-question {
            font-size: 1.125rem;
          }
        }
      `}</style>
    </div>
  )
}

export default FAQ
